/**
 * Centralized Style Resolver
 *
 * Single source of truth for all visual property resolution.
 * Eliminates duplicate size/color/variant/typography maps
 * scattered across 14+ renderer files.
 */
import type React from "react"

// ─── SIZE TOKENS ──────────────────────────────────────────────────
// Each component type has its own size scale, but all managed here.

/** Icon size: Tailwind h-/w- classes */
export const ICON_SIZE: Record<string, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  default: "h-4 w-4",
  md: "h-4 w-4",
  lg: "h-5 w-5",
  xl: "h-6 w-6",
  "2xl": "h-8 w-8",
  "3xl": "h-10 w-10",
  "4xl": "h-12 w-12",
}

/** Avatar size: Tailwind dimension + text classes */
export const AVATAR_SIZE: Record<string, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-lg",
}

/** Image size: Tailwind dimension classes */
export const IMAGE_SIZE: Record<string, string> = {
  xs: "w-8 h-8",
  sm: "w-12 h-12",
  thumbnail: "w-20 h-20",
  md: "w-32 h-32",
  lg: "w-48 h-48",
  xl: "w-64 h-64",
}

/** Star rating pixel sizes */
export const STAR_SIZE: Record<string, number> = {
  sm: 16,
  default: 24,
  md: 24,
  lg: 32,
}

// ─── TYPOGRAPHY TOKENS ────────────────────────────────────────────

/** Text size: Tailwind text-* classes */
export const TEXT_SIZE: Record<string, string> = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
  "5xl": "text-5xl",
  "6xl": "text-6xl",
  "7xl": "text-7xl",
  "8xl": "text-8xl",
  "9xl": "text-9xl",
}

/** Font weight: Tailwind font-* classes */
export const FONT_WEIGHT: Record<string, string> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
  extrabold: "font-extrabold",
}

/** Letter spacing / tracking: Tailwind tracking-* classes */
export const TRACKING: Record<string, string> = {
  tighter: "tracking-tighter",
  tight: "tracking-tight",
  normal: "tracking-normal",
  wide: "tracking-wide",
  wider: "tracking-wider",
  widest: "tracking-widest",
}

/** Text variant presets: combined typography classes */
export const TEXT_VARIANT: Record<string, string> = {
  "heading-xl": "text-3xl font-bold tracking-tight",
  "heading-lg": "text-2xl font-bold lg:text-3xl",
  "heading-md": "text-xl font-semibold",
  "heading-sm": "font-semibold leading-none tracking-tight",
  "body": "leading-relaxed",
  "body-sm": "text-sm",
  "caption": "text-xs text-muted-foreground",
  "price": "font-bold",
  "price-lg": "text-3xl font-bold",
  "price-strikethrough": "text-muted-foreground line-through",
  "muted": "text-muted-foreground",
}

/** Map numeric px → Tailwind text size token */
export function pxToTextSize(px: number): string {
  if (px >= 96) return "9xl"
  if (px >= 80) return "8xl"
  if (px >= 72) return "7xl"
  if (px >= 60) return "6xl"
  if (px >= 48) return "5xl"
  if (px >= 40) return "4xl"
  if (px >= 36) return "3xl"
  if (px >= 30) return "2xl"
  if (px >= 24) return "xl"
  if (px >= 20) return "lg"
  if (px >= 18) return "lg"
  if (px >= 16) return "base"
  if (px >= 14) return "sm"
  return "xs"
}

// ─── COLOR TOKENS ─────────────────────────────────────────────────
// Unified color resolution: theme tokens → Tailwind class,
// CSS values (hex/hsl/rgb/var) → inline style.

/** Theme color name → Tailwind text class */
export const TEXT_COLOR: Record<string, string> = {
  primary: "text-primary",
  "primary-foreground": "text-primary-foreground",
  foreground: "text-foreground",
  muted: "text-muted-foreground",
  "muted-foreground": "text-muted-foreground",
  "card-foreground": "text-card-foreground",
  "secondary-foreground": "text-secondary-foreground",
  "accent-foreground": "text-accent-foreground",
  destructive: "text-destructive",
  amber: "text-amber-400",
  white: "text-white",
  success: "text-green-600",
  warning: "text-amber-600",
}

/** Star/rating color */
export const STAR_COLOR = "#FACC15"

/**
 * Resolve a color token to either a Tailwind class or inline style.
 * Returns { className, style } — use whichever is non-empty.
 *
 * @example resolveColor("primary") → { className: "text-primary", style: undefined }
 * @example resolveColor("#FF0000") → { className: "", style: { color: "#FF0000" } }
 * @example resolveColor("hsl(var(--card))") → { className: "", style: { color: "hsl(var(--card))" } }
 */
export function resolveColor(
  color: string | undefined | null,
  prefix: "text" | "bg" | "border" = "text",
): { className: string; style?: React.CSSProperties } {
  if (!color) return { className: "" }

  // CSS values go to inline style
  if (color.startsWith("#") || color.startsWith("hsl") || color.startsWith("rgb") || color.startsWith("var(")) {
    const prop = prefix === "text" ? "color" : prefix === "bg" ? "backgroundColor" : "borderColor"
    return { className: "", style: { [prop]: color } }
  }

  // Theme token → Tailwind class
  if (prefix === "text") {
    return { className: TEXT_COLOR[color] ?? `text-${color}`, style: undefined }
  }
  return { className: `${prefix}-${color}`, style: undefined }
}

// ─── OBJECT FIT ───────────────────────────────────────────────────

export const OBJECT_FIT: Record<string, string> = {
  cover: "object-cover",
  contain: "object-contain",
  fill: "object-fill",
  none: "object-none",
}
